/**
 * Shared Price Monitoring (product_view) UI: route project key vs backend data source.
 * Extend {@link PRODUCT_VIEW_DATA_SOURCE_OVERRIDES} when another project should reuse an existing dataset.
 */

/** URL segment for Price Monitoring routes: `/:projectKey/price-monitoring/{retailers|brands|products}` */
export const PRODUCT_VIEW_ROUTE_SEGMENT = 'price-monitoring';

/** URL segment for Distribution View: `/:projectKey/distribution_view/{brands|retailers}` */
export const DISTRIBUTION_VIEW_ROUTE_SEGMENT = 'distribution_view';

/** Path param values → BatProductView `lineType` / Group by */
export const PRODUCT_VIEW_GROUP_BY_SEGMENTS = ['retailers', 'brands', 'products'];

export const DISTRIBUTION_VIEW_GROUP_BY_SEGMENTS = ['brands', 'retailers'];

const SEGMENT_TO_LINE_TYPE = {
  retailers: 'Retailers',
  brands: 'Brands',
  products: 'Products',
};

/**
 * @param {string | undefined} segment URL param under `price-monitoring/:segment`
 * @returns {'Retailers' | 'Brands' | 'Products' | null}
 */
export function getLineTypeFromPriceMonitoringSegment(segment) {
  if (!segment) return null;
  return SEGMENT_TO_LINE_TYPE[segment] ?? null;
}

/**
 * @param {string | undefined} segment URL param under `distribution_view/:segment`
 * @returns {boolean}
 */
export function isDistributionViewGroupBySegment(segment) {
  return DISTRIBUTION_VIEW_GROUP_BY_SEGMENTS.includes(segment);
}

/** Maps route :projectKey → API project_key used for /{project_key}/product_view/... */
const PRODUCT_VIEW_DATA_SOURCE_OVERRIDES = {
  'jti-sweden': 'bat',
};

/**
 * @param {string | undefined} routeProjectKey from the URL
 * @returns {string} project key for product_view API calls (page_data, raw_data, etc.)
 */
export function getProductViewDataProjectKey(routeProjectKey) {
  if (!routeProjectKey) return routeProjectKey;
  return PRODUCT_VIEW_DATA_SOURCE_OVERRIDES[routeProjectKey] ?? routeProjectKey;
}

/**
 * Download filename for chart CSV export (avoid client-specific naming where requested).
 * @param {string | undefined} routeProjectKey
 */
export function getProductViewPriceExportFilename(routeProjectKey) {
  if (routeProjectKey === 'jti-sweden') {
    return 'price_data.csv';
  }
  return 'bat_price_data.csv';
}

/** BAT / default product_view chrome */
const ACCENT_BAT = {
  main: '#24336B',
  hover: '#1a2550',
  rail: '#c0c8e0',
};

const ACCENT_PMI = {
  main: 'rgb(0, 116, 194)',
  hover: '#005e9d',
  rail: '#bfdcf4',
};

/** JTI white-label (matches sidebar greens) */
const ACCENT_JTI_SWEDEN_WHITELABEL = {
  main: '#2f8f4e',
  hover: '#256a3d',
  rail: '#c5e0d0',
};

/** Page-level accent tokens (matches JTIPriceAndPromotionsView STYLE_PRESETS). */
const STYLE_PRESETS = {
  classic: { accent: '#2e7d32' },
  whiteLabel: { accent: '#3d8f55' },
  batWhiteLabel: { accent: '#24336B' },
  pmiWhiteLabel: { accent: 'rgb(0, 116, 194)' },
};

/**
 * Accent color for MUI primary palette on JTI/BAT/PMI data views.
 * @param {string | undefined} routeProjectKey
 * @param {string | undefined} uiStyle 'white-label' | 'classic'
 */
export function getStyleTokens(routeProjectKey, uiStyle) {
  if (uiStyle === 'white-label') {
    if (routeProjectKey === 'pmi') return STYLE_PRESETS.pmiWhiteLabel;
    if (routeProjectKey === 'bat') return STYLE_PRESETS.batWhiteLabel;
    return STYLE_PRESETS.whiteLabel;
  }
  return STYLE_PRESETS.classic;
}

/**
 * Refresh button + date slider colors: dark blue for BAT; JTI Sweden green when white-label.
 * @param {string | undefined} routeProjectKey
 * @param {string | undefined} uiStyle 'white-label' | 'classic'
 */
export function getProductViewAccent(routeProjectKey, uiStyle) {
  if (routeProjectKey === 'bat') {
    return ACCENT_BAT;
  }
  if (routeProjectKey === 'pmi') {
    return ACCENT_PMI;
  }
  if (routeProjectKey === 'jti-sweden' && uiStyle === 'white-label') {
    return ACCENT_JTI_SWEDEN_WHITELABEL;
  }
  return ACCENT_BAT;
}
